import React, {
  useEffect,
  useState,
} from "react";

import {
  offerMasterAPI,
} from "../../../api/client";

import {
  toast,
} from "react-toastify";

const initialForm = {
  training_amount: "",
  internship_period: "",
  internship_stipend: "",

  year1_fixed: "",
  year1_variable_type: "",
  year1_variable_amount: "",
  year1_ctc: "",

  year2_fixed: "",
  year2_variable_type: "",
  year2_variable_amount: "",
  year2_ctc: "",

  year3_fixed: "",
  year3_variable_type: "",
  year3_variable_amount: "",
  year3_ctc: "",

  year4_fixed: "",
  year4_variable_type: "",
  year4_variable_amount: "",
  year4_ctc: "",
};

const VARIABLE_TYPES = [
  "Monthly Bonus",
  "Quarterly Bonus",
  "Half Yearly Bonus",
  "Yearly Bonus",
  "Fixed",
  "Performance",
];

function numberOrNull(value) {
  if (
    value === undefined ||
    value === null ||
    String(value).trim() === ""
  ) {
    return null;
  }

  const number = Number(value);

  return Number.isFinite(number)
    ? number
    : null;
}

function mapMasterToForm(data) {
  if (!data) {
    return initialForm;
  }

  return {
    training_amount:
      data.training_amount ?? "",

    internship_period:
      data.internship_period ?? "",

    internship_stipend:
      data.internship_stipend ?? "",

    year1_fixed:
      data.year1_fixed ?? "",

    year1_variable_type:
      data.year1_variable_type ?? "",

    year1_variable_amount:
      data.year1_variable_amount ?? "",

    year1_ctc:
      data.year1_ctc ?? "",

    year2_fixed:
      data.year2_fixed ?? "",

    year2_variable_type:
      data.year2_variable_type ?? "",

    year2_variable_amount:
      data.year2_variable_amount ?? "",

    year2_ctc:
      data.year2_ctc ?? "",

    year3_fixed:
      data.year3_fixed ?? "",

    year3_variable_type:
      data.year3_variable_type ?? "",

    year3_variable_amount:
      data.year3_variable_amount ?? "",

    year3_ctc:
      data.year3_ctc ?? "",

    year4_fixed:
      data.year4_fixed ?? "",

    year4_variable_type:
      data.year4_variable_type ?? "",

    year4_variable_amount:
      data.year4_variable_amount ?? "",

    year4_ctc:
      data.year4_ctc ?? "",
  };
}

export default function ConditionInputPage() {
  const [
    form,
    setForm,
  ] = useState(initialForm);

  const [
    recordId,
    setRecordId,
  ] = useState(null);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    saving,
    setSaving,
  ] = useState(false);

  /*
  ====================================================
  LOAD MASTER
  ====================================================
  */

  const loadMaster = async () => {
    try {
      setLoading(true);

      const response =
        await offerMasterAPI
          .getConditionalOfferMaster();

      const data =
        response?.data ?? null;

      if (!data) {
        setRecordId(null);
        setForm(initialForm);
        return;
      }

      setRecordId(
        data.id ?? null
      );

      setForm(
        mapMasterToForm(data)
      );

    } catch (error) {
      console.error(
        "Failed to load conditional offer master:",
        error
      );

      toast.error(
        error?.response?.data?.error ||
          "Failed to load conditional offer master"
      );

    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMaster();
  }, []);

  /*
  ====================================================
  CHANGE
  ====================================================
  */

  const handleChange = (event) => {
    const {
      name,
      value,
    } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  /*
  ====================================================
  VALIDATION
  ====================================================
  */

  const validateForm = () => {
    if (
      form.training_amount === "" ||
      !Number.isFinite(
        Number(form.training_amount)
      )
    ) {
      toast.error(
        "Training amount is required and must be a valid number"
      );

      return false;
    }

    if (
      !form.internship_period.trim()
    ) {
      toast.error(
        "Internship period is required"
      );

      return false;
    }

    if (
      form.internship_stipend === "" ||
      !Number.isFinite(
        Number(form.internship_stipend)
      )
    ) {
      toast.error(
        "Internship stipend is required and must be a valid number"
      );

      return false;
    }

    const numericFields = [
      "year1_fixed",
      "year1_variable_amount",
      "year1_ctc",

      "year2_fixed",
      "year2_variable_amount",
      "year2_ctc",

      "year3_fixed",
      "year3_variable_amount",
      "year3_ctc",

      "year4_fixed",
      "year4_variable_amount",
      "year4_ctc",
    ];

    for (const field of numericFields) {
      const value = form[field];

      if (
        value !== "" &&
        !Number.isFinite(Number(value))
      ) {
        toast.error(
          `${field} must be a valid number`
        );

        return false;
      }

      if (
        value !== "" &&
        Number(value) < 0
      ) {
        toast.error(
          `${field} cannot be negative`
        );

        return false;
      }
    }

    return true;
  };

  /*
  ====================================================
  PAYLOAD
  ====================================================
  */

  const buildPayload = () => ({
    training_amount:
      Number(form.training_amount),

    internship_period:
      form.internship_period.trim(),

    internship_stipend:
      Number(form.internship_stipend),

    year1_fixed:
      numberOrNull(form.year1_fixed),

    year1_variable_type:
      form.year1_variable_type.trim() ||
      null,

    year1_variable_amount:
      numberOrNull(
        form.year1_variable_amount
      ),

    year1_ctc:
      numberOrNull(form.year1_ctc),

    year2_fixed:
      numberOrNull(form.year2_fixed),

    year2_variable_type:
      form.year2_variable_type.trim() ||
      null,

    year2_variable_amount:
      numberOrNull(
        form.year2_variable_amount
      ),

    year2_ctc:
      numberOrNull(form.year2_ctc),

    year3_fixed:
      numberOrNull(form.year3_fixed),

    year3_variable_type:
      form.year3_variable_type.trim() ||
      null,

    year3_variable_amount:
      numberOrNull(
        form.year3_variable_amount
      ),

    year3_ctc:
      numberOrNull(form.year3_ctc),

    year4_fixed:
      numberOrNull(form.year4_fixed),

    year4_variable_type:
      form.year4_variable_type.trim() ||
      null,

    year4_variable_amount:
      numberOrNull(
        form.year4_variable_amount
      ),

    year4_ctc:
      numberOrNull(form.year4_ctc),
  });

  /*
  ====================================================
  SAVE
  ====================================================
  */

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);

      const payload =
        buildPayload();

      console.log(
        "CONDITIONAL OFFER PAYLOAD:",
        payload
      );

      if (recordId) {
        await offerMasterAPI
          .updateConditionalOfferMaster(
            recordId,
            payload
          );

        toast.success(
          "Conditional offer master updated successfully"
        );

      } else {
        const response =
          await offerMasterAPI
            .createConditionalOfferMaster(
              payload
            );

        const newId =
          response?.data?.id ?? null;

        setRecordId(newId);

        toast.success(
          "Conditional offer master created successfully"
        );
      }

      await loadMaster();

    } catch (error) {
      console.error(
        "Save conditional offer master error:",
        error
      );

      const details =
        error?.response?.data?.details;

      if (
        Array.isArray(details) &&
        details.length > 0
      ) {
        toast.error(
          details.join(", ")
        );
      } else {
        toast.error(
          error?.response?.data?.error ||
            "Failed to save conditional offer master"
        );
      }

    } finally {
      setSaving(false);
    }
  };

  /*
  ====================================================
  RESET
  ====================================================
  */

  const handleReset = async () => {
    await loadMaster();
  };

  /*
  ====================================================
  DELETE
  ====================================================
  */

  const handleDelete = async () => {
    if (!recordId) {
      return;
    }

    const confirmed =
      window.confirm(
        "Delete the conditional offer master?"
      );

    if (!confirmed) {
      return;
    }

    try {
      setSaving(true);

      await offerMasterAPI
        .deleteConditionalOfferMaster(
          recordId
        );

      setRecordId(null);
      setForm(initialForm);

      toast.success(
        "Conditional offer master deleted successfully"
      );

    } catch (error) {
      console.error(
        "Delete conditional offer master error:",
        error
      );

      toast.error(
        error?.response?.data?.error ||
          "Failed to delete conditional offer master"
      );

    } finally {
      setSaving(false);
    }
  };

  /*
  ====================================================
  YEAR SECTION
  ====================================================
  */

  const renderYearSection = (
    year
  ) => {
    const prefix =
      `year${year}`;

    return (
      <div
        className="conditional-year-card"
        key={year}
      >

        <div className="conditional-year-header">
          <h3>
            Year {year}
          </h3>
        </div>

        <div className="conditional-form-grid">

          <div className="conditional-field">

            <label>
              Fixed Amount
            </label>

            <input
              type="number"
              min="0"
              step="0.01"
              name={`${prefix}_fixed`}
              value={
                form[
                  `${prefix}_fixed`
                ]
              }
              onChange={handleChange}
              placeholder="Enter fixed amount"
              disabled={saving}
            />

          </div>


          <div className="conditional-field">

            <label>
              Variable Type
            </label>

            <select
              name={`${prefix}_variable_type`}
              value={
                form[
                  `${prefix}_variable_type`
                ]
              }
              onChange={handleChange}
              disabled={saving}
            >

              <option value="">
                Select variable type
              </option>

              {VARIABLE_TYPES.map(
                (type) => (
                  <option
                    key={type}
                    value={type}
                  >
                    {type}
                  </option>
                )
              )}

            </select>

          </div>


          <div className="conditional-field">

            <label>
              Variable Amount
            </label>

            <input
              type="number"
              min="0"
              step="0.01"
              name={`${prefix}_variable_amount`}
              value={
                form[
                  `${prefix}_variable_amount`
                ]
              }
              onChange={handleChange}
              placeholder="Enter variable amount"
              disabled={saving}
            />

          </div>


          <div className="conditional-field">

            <label>
              CTC
            </label>

            <input
              type="number"
              min="0"
              step="0.01"
              name={`${prefix}_ctc`}
              value={
                form[
                  `${prefix}_ctc`
                ]
              }
              onChange={handleChange}
              placeholder="Enter CTC"
              disabled={saving}
            />

          </div>

        </div>

      </div>
    );
  };

  /*
  ====================================================
  RENDER
  ====================================================
  */

  if (loading) {
    return (
      <div className="conditional-offer-page">

        <div className="conditional-section">

          <div className="workflow-loading">
            Loading conditional offer master...
          </div>

        </div>

      </div>
    );
  }

  return (
    <div className="conditional-offer-page">

      <div className="conditional-offer-header">

        <div>
          <h2>
            Conditional Offer Master
          </h2>

          <p>
            Configure training, internship
            and year-wise compensation
            details used for conditional offers.
          </p>
        </div>

        {recordId && (
          <span className="conditional-record-badge">
            Master configured
          </span>
        )}

      </div>


      <form
        className="conditional-offer-form"
        onSubmit={handleSubmit}
      >

        {/* =================================================
            TRAINING / INTERNSHIP
        ================================================== */}

        <div className="conditional-section">

          <div className="conditional-section-title">

            <span className="conditional-section-number">
              1
            </span>

            <div>
              <h3>
                Training & Internship
              </h3>

              <p>
                Enter training and internship
                compensation details.
              </p>
            </div>

          </div>


          <div className="conditional-form-grid">

            <div className="conditional-field">

              <label>
                Training Amount
                <span>*</span>
              </label>

              <input
                type="number"
                name="training_amount"
                value={
                  form.training_amount
                }
                onChange={handleChange}
                min="0"
                step="0.01"
                required
                disabled={saving}
                placeholder="Enter training amount"
              />

            </div>


            <div className="conditional-field">

              <label>
                Internship Period
                <span>*</span>
              </label>

              <input
                type="text"
                name="internship_period"
                value={
                  form.internship_period
                }
                onChange={handleChange}
                maxLength={50}
                required
                disabled={saving}
                placeholder="Example: 6 Months"
              />

            </div>


            <div className="conditional-field">

              <label>
                Internship Stipend
                <span>*</span>
              </label>

              <input
                type="number"
                name="internship_stipend"
                value={
                  form.internship_stipend
                }
                onChange={handleChange}
                min="0"
                step="0.01"
                required
                disabled={saving}
                placeholder="Enter stipend amount"
              />

            </div>

          </div>

        </div>


        {/* =================================================
            YEAR-WISE
        ================================================== */}

        <div className="conditional-section">

          <div className="conditional-section-title">

            <span className="conditional-section-number">
              2
            </span>

            <div>
              <h3>
                Year-wise Compensation
              </h3>

              <p>
                Configure fixed, variable
                and CTC values for each year.
              </p>
            </div>

          </div>


          {renderYearSection(1)}
          {renderYearSection(2)}
          {renderYearSection(3)}
          {renderYearSection(4)}

        </div>


        {/* =================================================
            FOOTER
        ================================================== */}

        <div className="conditional-form-footer">

          <button
            type="button"
            className="conditional-reset-button"
            onClick={handleReset}
            disabled={saving}
          >
            Reset
          </button>


          {recordId && (
            <button
              type="button"
              className="conditional-delete-button"
              onClick={handleDelete}
              disabled={saving}
            >
              Delete
            </button>
          )}


          <button
            type="submit"
            className="conditional-save-button"
            disabled={saving}
          >
            {saving
              ? "Saving..."
              : recordId
              ? "Update Conditional Offer"
              : "Save Conditional Offer"}
          </button>

        </div>

      </form>

    </div>
  );
}